import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import get from 'lodash.get';
import classNames from 'classnames';
import { COLUMNS_NAMES } from '../../../common/utils/studentsSortingHelpers';
import { DIBELS8, READING } from '../../../constants/applications';
import {
    MEASURE_BY_SKILL_NAME,
    MEASURE_SCORE_BY_SKILL_NAME,
    MEASURE_SCORE_DISPLAY_NAMES,
    SKILL_NAMES_BY_APPLICATION,
    D8_SUBMEASURES,
    SKILL_NAMES,
    ASSESSMENT_IMPORTER_DATA_SOURCE,
    ADMINISTRATION_SCHEDULE,
} from '../../../constants/assessments';
import { PERIOD, GRADE } from '../../../constants/enrollment';
import AssessLink from '../assess-link/AssessLink';
import D8MeasureScore from './D8MeasureScore';
import { getMeasureScores } from '../../../common/utils/studentsListHelpers';
import { resultType } from '../../../constants/prop-types';
import {
    constructDibels8Link,
    PAGE_TYPE,
    linkOnClickHandler,
} from '../../../common/navigation/navigation';
import { getGatingRules } from '../../../common/gating-rules/getGatingRules';
import { useAppContext } from '../../../common/contexts/AppContext';
import { usePopupContext } from '../../../common/contexts/PopupContext';
import { useAppConfigContext } from '../../../common/contexts/AppConfigContext';
import MeasureBannersContainer from '../../banner/MeasureBannersContainer';
import InfoIcon from '../info-icon/InfoIcon';
import { useBannerContext } from '../../../common/contexts/BannerContext';
import TableHeader from '../table-header/TableHeader';
import HyphenIcon from '../hyphen-icon/HyphenIcon';
import CompositeScoreRow from '../composite-score-row/CompositeScoreRow';
import { hasAlternativeForms as getHasAlternativeForms } from '../../../common/utils/alternateFormsHelpers';
import useAssessIcons from '../../../common/hooks/useAssessIcons';
import { useFeatureToggleContext } from '../../../common/contexts/FeatureToggleContext';
import getAvailableBenchmarkPeriods from '../../../common/utils/getAvailableBenchmarkPeriods';
import { handleEnterKeyPress } from '../../../common/popups/utils';

const DIBELS8_SKILLS = SKILL_NAMES_BY_APPLICATION[DIBELS8];

const DISABLED_ICONS = {
    [AssessLink.MODE.OPTIONAL]: AssessLink.MODE.OPTIONAL_DISABLED,
    [AssessLink.MODE.DISCONTINUE]: AssessLink.MODE.DISCONTINUE_DISABLED,
    [AssessLink.MODE.TESTED_OUT]: AssessLink.MODE.TESTED_OUT_DISABLED,
    [AssessLink.MODE.REQUIRED]: AssessLink.MODE.DISABLED,
    [AssessLink.MODE.DISABLED]: AssessLink.MODE.DISABLED,
};

const getAssessLinkMode = (gatingRules, measureName, isOptional, isGatingRuleEnabled) => {
    if (isGatingRuleEnabled) {
        if (gatingRules.isRuleMet && gatingRules.gatedMeasures.includes(measureName)) {
            return AssessLink.MODE[gatingRules.ruleType];
        }

        if (gatingRules.isRuleMet === null && gatingRules.gatedMeasures.includes(measureName)) {
            return AssessLink.MODE.DISABLED;
        }
    }

    return isOptional ? AssessLink.MODE.OPTIONAL : AssessLink.MODE.REQUIRED;
};

const getIsOrfG5BoyFormPopupApplicable = (skillName, period, grade, isPopupEnabled) => {
    const { READING_FLUENCY } = SKILL_NAMES;
    const { BOY } = PERIOD;
    const { FIVE } = GRADE;
    if (
        skillName === READING_FLUENCY &&
        period === BOY &&
        grade === FIVE &&
        isPopupEnabled !== false
    ) {
        return true;
    }
    return false;
};

const getIsGatedMeasure = (gatingRules, measureName) => {
    return (
        gatingRules.gatedMeasures.includes(measureName) &&
        (gatingRules.isRuleMet || gatingRules.isRuleMet === null)
    );
};

const isHyphenIcon = (skillName, period) =>
    SKILL_NAMES.BASIC_COMPREHENSION === skillName ||
    D8_SUBMEASURES.includes(skillName) ||
    (skillName === SKILL_NAMES.FIRST_SOUND_ISOLATION && period !== PERIOD.BOY);

const getSkillDetailsForPeriod = (
    skillName,
    period,
    { student, classId, results },
    formsMap,
    popupActions,
    isOrfG5BoyFormPopupEnable,
    isOptional,
    isBMAssessmentIconEnabled,
    isBMAssessmentIconVisible,
    gatingRulesConfig,
    isGatingRulesRegulationEnabled,
) => {
    const { id, grade, appStatuses } = student;
    const measure = MEASURE_BY_SKILL_NAME[skillName];
    const measureScoreName = MEASURE_SCORE_BY_SKILL_NAME[skillName];
    const assessmentLink = constructDibels8Link(id, measure, PAGE_TYPE.REMINDERS, {
        period,
        classId,
    });

    const studentResults = get(results, `${period}.${id}`, []);
    const gatedMeasuresStatus = get(appStatuses.dibels8[period], 'gatedMeasuresStatus', {});
    const gatingRules = getGatingRules(studentResults, grade, period, gatedMeasuresStatus, DIBELS8);
    const measureScores = getMeasureScores(studentResults, measure, measureScoreName);
    const key = `${skillName}${period}`;
    const isFromAssessmentImporter =
        measureScores && measureScores.dataSource === ASSESSMENT_IMPORTER_DATA_SOURCE;

    if (!measureScores && isBMAssessmentIconVisible(period)) {
        if (isHyphenIcon(skillName, period)) {
            return <HyphenIcon key={key} />;
        }

        const isAssessmentEnabled = isBMAssessmentIconEnabled(period);
        const hasAlternativeForms = getHasAlternativeForms(studentResults, measure, DIBELS8);
        const isGatedMeasure = getIsGatedMeasure(gatingRules, measureScoreName);

        const isGatingRuleEnabled = isGatingRulesRegulationEnabled
            ? !get(gatingRulesConfig, `${grade}`, []).includes(period)
            : true;

        const assessmentLinkMode = getAssessLinkMode(
            gatingRules,
            measureScoreName,
            isOptional,
            isGatingRuleEnabled,
        );
        const assessLinkMode = isAssessmentEnabled
            ? assessmentLinkMode
            : DISABLED_ICONS[assessmentLinkMode];

        const isOrfG5BoyFormPopupApplicable = getIsOrfG5BoyFormPopupApplicable(
            skillName,
            period,
            grade,
            isOrfG5BoyFormPopupEnable,
        );

        const launchOrfG5BoyFormPopup = linkToAssessment => {
            popupActions.showOrfG5BoyFormPopup({
                linkToAssessment,
            });
        };

        const onAltFormSelected = (linkToAssessment, selectedForm, checkAndGetSessionStatus, e) => {
            if (isOrfG5BoyFormPopupApplicable && selectedForm.period !== PERIOD.PM) {
                launchOrfG5BoyFormPopup(linkToAssessment);
            } else {
                linkOnClickHandler(linkToAssessment, checkAndGetSessionStatus)(e);
            }
        };

        const launchAlternateFormSelectionPopup = () => {
            popupActions.showD8AlternateFormSelectionPopup({
                onFormSelected: onAltFormSelected,
                grade,
                studentId: id,
                period,
                skillName,
                measureName: measure,
            });
        };

        const startAssmentFormOrfG5BoyFormPopup = () => {
            launchOrfG5BoyFormPopup(assessmentLink);
        };

        if (isGatingRuleEnabled && isGatedMeasure) {
            if (
                [AssessLink.MODE.DISCONTINUE, AssessLink.MODE.TESTED_OUT].includes(assessLinkMode)
            ) {
                const startAssessment = linkOnClickHandler(assessmentLink);
                const onGatedMeasureNoticePopupContinue = hasAlternativeForms
                    ? launchAlternateFormSelectionPopup
                    : startAssessment;
                const launchGatedMeasureNoticePopup = () => {
                    popupActions.showGatedMeasureNoticePopup({
                        name: assessLinkMode,
                        onContinueClick: onGatedMeasureNoticePopupContinue,
                    });
                };
                return (
                    <AssessLink
                        key={key}
                        mode={assessLinkMode}
                        onClickHandler={launchGatedMeasureNoticePopup}
                        onKeyDown={handleEnterKeyPress}
                    />
                );
            }
        } else if (hasAlternativeForms) {
            return (
                <AssessLink
                    key={key}
                    mode={assessLinkMode}
                    onClickHandler={launchAlternateFormSelectionPopup}
                    onKeyDown={handleEnterKeyPress}
                />
            );
        } else if (isOrfG5BoyFormPopupApplicable) {
            return (
                <AssessLink
                    key={key}
                    mode={assessLinkMode}
                    onClickHandler={startAssmentFormOrfG5BoyFormPopup}
                    onKeyDown={handleEnterKeyPress}
                />
            );
        }
        return <AssessLink key={key} mode={assessLinkMode} path={assessmentLink} />;
    }

    if (!measureScores) {
        return <div key={key} />;
    }

    return (
        <D8MeasureScore
            isFromAssessmentImporter={isFromAssessmentImporter}
            interpretation={measureScores && measureScores.interpretation}
            score={measureScores && measureScores.rawScore}
            studentId={student.id}
            measure={measure}
            period={period}
            classId={classId}
            resultId={measureScores.resultId}
            isFormAvailable={formsMap.hasOwnProperty(measureScores.formUid)}
            key={key}
        />
    );
};

const getD8LicensedSkills = (licensedSkills, grade) =>
    get(ADMINISTRATION_SCHEDULE, grade, []).filter(admSkillName =>
        licensedSkills.find(
            ({ skillName, grades }) => skillName === admSkillName && grades.includes(grade),
        ),
    );

const Dibels8Summary = props => {
    const grade = get(props, 'student.grade', null);
    const { student, classId, results } = props;
    const {
        bmWindow: { period: currentPeriod },
        formsMap,
        gatingRulesConfig,
        fourthPeriodAvailability,
    } = useAppContext();

    const benchmarkPeriods = useMemo(
        () => getAvailableBenchmarkPeriods(READING, fourthPeriodAvailability),
        [fourthPeriodAvailability],
    );

    const { isGatingRulesRegulationEnabled } = useFeatureToggleContext();

    const { isBMAssessmentIconEnabled, isBMAssessmentIconVisible } = useAssessIcons(
        benchmarkPeriods,
    );

    const { actions: popupActions } = usePopupContext();
    const {
        actions: {
            showBMMazeBanner,
            showBMLNFPSFBanner,
            enableMazeBanner,
            enableLNFPSFBanner,
            showSummerBanner,
        },
        isSummerPeriodAvailable,
    } = useBannerContext();
    const licensedMeasures = get(
        useAppConfigContext(),
        ['appConfigs', DIBELS8, 'licensedMeasures'],
        [],
    );

    const licensedSkillsForGrade = getD8LicensedSkills(licensedMeasures, grade);

    const dyslexiaSkillNamesInGradeTwo = useMemo(
        () =>
            licensedSkillsForGrade.filter(
                skillName =>
                    [SKILL_NAMES.LETTER_NAMES, SKILL_NAMES.PHONEMIC_AWARENESS].includes(
                        skillName,
                    ) && grade === GRADE.TWO,
            ),
        [licensedSkillsForGrade, grade],
    );

    const isLNFPSFMeasuresInGradeTwoAvailable = dyslexiaSkillNamesInGradeTwo.length !== 0;

    useEffect(() => {
        if (isLNFPSFMeasuresInGradeTwoAvailable) {
            enableLNFPSFBanner({
                classId,
                studentUid: student.id,
                skillNames: dyslexiaSkillNamesInGradeTwo,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLNFPSFMeasuresInGradeTwoAvailable]);

    const { userPopupsSettings } = usePopupContext();
    // if Maze is in administration schedule
    // but user haven't completed Maze results in current period
    // app should show a banner informing user that Maze is
    // not observational measure
    const isMazeAvailable = useMemo(
        () =>
            licensedSkillsForGrade.includes(SKILL_NAMES.BASIC_COMPREHENSION) &&
            !get(results, `${currentPeriod}.${student.id}`, []).some(
                result =>
                    result.skillName === SKILL_NAMES.BASIC_COMPREHENSION &&
                    result.isComplete &&
                    result.isValid,
            ),
        [licensedSkillsForGrade, results, currentPeriod, student.id],
    );

    useEffect(() => {
        enableMazeBanner({
            classId,
            studentUid: student.id,
            isAvailable: isMazeAvailable,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMazeAvailable]);

    const showMazeBanner = () => showBMMazeBanner({ classId, studentUid: student.id });
    const showLNFPSFBanner = () => showBMLNFPSFBanner({ classId, studentUid: student.id });
    const showSummer = () => showSummerBanner({ classId, studentUid: student.id });
    const hasFourthPeriod = benchmarkPeriods.length > 3;

    const isMazeSummerAvailable = (isSummerPeriodAvailable() && isMazeAvailable) || isMazeAvailable;
    const showBanner = !isSummerPeriodAvailable() ? showMazeBanner : showSummer;

    return (
        <>
            <TableHeader
                columns={[COLUMNS_NAMES.DIBELS, ...benchmarkPeriods]}
                hasFourthPeriod={hasFourthPeriod}
                showFourthPeriodLabel
            />
            <MeasureBannersContainer studentUid={student.id} classId={classId} />
            <CompositeScoreRow
                appName={DIBELS8}
                student={student}
                benchmarkPeriods={benchmarkPeriods}
                hasFourthPeriod={hasFourthPeriod}
            />

            <div className='dashboard-measure-scores' data-test-id="dibels8-measure-scores">
                {DIBELS8_SKILLS.filter(skillName => licensedSkillsForGrade.includes(skillName)).map(
                    skillName => (
                        <div
                            className={classNames('dashboard-measure-info', {
                                'dashboard-has-fourth-period': hasFourthPeriod,
                            })}
                            key={skillName}
                        >
                            <div className='dashboard-skill'>
                                <span
                                    className={classNames('dashboard-skill-name', {
                                        'dashboard-optional-skill':
                                            skillName === SKILL_NAMES.FIRST_SOUND_ISOLATION,
                                    })}
                                >
                                    {skillName}
                                </span>
                                <span className='dashboard-measure-name'>
                                    {
                                        MEASURE_SCORE_DISPLAY_NAMES[
                                            MEASURE_SCORE_BY_SKILL_NAME[skillName]
                                        ]
                                    }
                                </span>
                                {skillName === SKILL_NAMES.BASIC_COMPREHENSION &&
                                    isMazeSummerAvailable && (
                                        <InfoIcon onClickHandler={showBanner} />
                                    )}

                                {isLNFPSFMeasuresInGradeTwoAvailable &&
                                    [
                                        SKILL_NAMES.LETTER_NAMES,
                                        SKILL_NAMES.PHONEMIC_AWARENESS,
                                    ].includes(skillName) && (
                                        <InfoIcon onClickHandler={showLNFPSFBanner} />
                                    )}
                            </div>

                            {benchmarkPeriods.map(period =>
                                getSkillDetailsForPeriod(
                                    skillName,
                                    period,
                                    {
                                        student,
                                        classId,
                                        results,
                                    },
                                    formsMap,
                                    popupActions,
                                    userPopupsSettings.isOrfG5BoyFormPopupEnable,
                                    skillName === SKILL_NAMES.FIRST_SOUND_ISOLATION,
                                    isBMAssessmentIconEnabled,
                                    isBMAssessmentIconVisible,
                                    gatingRulesConfig,
                                    isGatingRulesRegulationEnabled,
                                ),
                            )}
                        </div>
                    ),
                )}
            </div>

            <div className='dashboard-empty-divider' />
        </>
    );
};

Dibels8Summary.propTypes = {
    student: PropTypes.object.isRequired,
    classId: PropTypes.string.isRequired,
    results: PropTypes.shape({
        BOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        MOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        EOY: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
        PM: PropTypes.objectOf(PropTypes.arrayOf(resultType)),
    }),
};

Dibels8Summary.defaultProps = {
    results: {},
};

export default Dibels8Summary;
